import { Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Loading from "@/common/components/ui/loading/Loading";
import {
    ExclusionEntityType,
    getFinanceExclusionHistoryDetails,
} from "@/common/services/finance-exclusion-history.api";

function formatMoneyBR(value: unknown) {
    if (typeof value !== "number" || Number.isNaN(value)) return "-";
    return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
}

type DetailItem = {
    id: string;
    entityType: ExclusionEntityType;

    renterId?: string | null;
    renterName ?: string | null;

    description?: string | null;
    value?: number | null;

    reason?: string | null;

    authorName?: string | null;

    invoiceUrl?: string | null; 
};

export default function ExclusionHistoryDetailsPage() {
    const navigate = useNavigate();
    const { entityType, id } = useParams();

    const [loading, setLoading] = useState(true);
    const [item, setItem] = useState<DetailItem | null>(null);

    useEffect(() => {
        async function load() {
            if (!entityType || !id) {
                setLoading(false);
                setItem(null);
                return;
            }

            setLoading(true);
            const result = await getFinanceExclusionHistoryDetails({
                entityType: entityType as ExclusionEntityType,
                id,
            });
            setLoading(false);

            if (!result.ok) {
                setItem(null);
                return;
            }

            if (result?.ok && result.data) {
                setItem(result.data as DetailItem);
            } else {
                setItem(null);
            }
        }

        load();
    }, [entityType, id]);

    const author = item?.authorName ?? "-";
    const canOpenClient = Boolean(item?.renterId);

    const isPayment = item?.entityType === "PAYMENT";
    const invoiceUrl = isPayment ? item?.invoiceUrl ?? null : null;

    return (
        <Suspense fallback={<Loading />}>
            <div className="table-section">
                {}
                <div className="flex items-center justify-between gap-4 bg-white rounded-lg p-4 shadow-sm w-full">
                    <div className="min-w-0">
                        <h1 className="text-xl md:text-2xl font-semibold truncate">
                            Detalhes da Exclusão
                        </h1>
                        <p className="text-sm text-gray-500 truncate">
                            Visualize as informações da exclusão.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="h-10 px-4 rounded-md bg-redPrimary text-white hover:opacity-90 shrink-0"
                        onClick={() => navigate(-1)}
                    >
                        Voltar
                    </button>
                </div>

                <div className="h-4" />

                {loading ? (
                    <Loading />
                ) : !item ? (
                    <div className="bg-white rounded-lg p-6 shadow-sm w-full">
                        Registro não encontrado.
                    </div>
                ) : (
                    <div className="bg-white rounded-lg p-6 shadow-sm w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Coluna esquerda */}
                            <div className="rounded-lg border border-gray-100 p-5">
                                <h2 className="text-base font-semibold mb-4">
                                    Cliente / Exclusão
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Cliente</div>
                                        <div
                                            className={`font-semibold text-gray-900 ${canOpenClient
                                                    ? "hover:text-bluePrimary cursor-pointer transition-colors"
                                                    : ""
                                                }`}
                                            onClick={() => {
                                                if (item.renterId) navigate(`/clientes/${item.renterId}`);
                                            }}
                                            title={canOpenClient ? "Abrir cliente" : undefined}
                                        >
                                            {item.renterName ?? "-"}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Autor</div>
                                        <div className="font-medium">{author}</div>
                                    </div>

                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Motivo</div>
                                        <div className="font-medium">{item.reason ?? "-"}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Coluna direita */}
                            <div className="rounded-lg border border-gray-100 p-5">
                                <h2 className="text-base font-semibold mb-4">
                                    {item.entityType === "PAYMENT"
                                        ? "Dados do Pagamento"
                                        : "Dados da Assinatura"}
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Descrição</div>
                                        <div className="font-medium">{item.description ?? "-"}</div>
                                    </div>

                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Valor</div>
                                        <div className="font-medium">
                                            {item.value != null ? `R$ ${formatMoneyBR(item.value)}` : "-"}
                                        </div>
                                    </div>

                                    {/* Payment */}
                                    {isPayment ? (
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Fatura (Asaas)</div>
                                            {invoiceUrl ? (
                                                <a
                                                    href={invoiceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-medium text-bluePrimary hover:underline break-all"
                                                    title="Abrir fatura no Asaas"
                                                >
                                                    Abrir fatura
                                                </a>
                                            ) : (
                                                <div className="font-medium">-</div>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Suspense>
    );
}
